<template>
    <div
        v-click-outside="close"
        class="date-input-wrap"
        :class="{ 'has-value': selected.start && selected.end }"
        @click="showPicker = !showPicker"
    >
        <span v-if="label">{{ label }}</span>
        <div class="date-input date-range-input">
            <div class="date-input__field">
                <span>
                    {{ dateText ? dateText : $t('misc.date') }}
                </span>
                <button
                    v-if="selected.start && selected.end && clearable"
                    type="button"
                    class="date-input__clear"
                    @click.stop="cancel"
                >
                    ✕
                </button>
            </div>
            <transition name="fade">
                <div v-if="showPicker" class="date-range-input__dropdown">
                    <div class="date-range-input__pickers">
                        <div class="date-range-input__picker-wrap">
                            <span>{{ $t('dateRange.dateStart') }}</span>
                            <Datepicker
                                class="date-range-input__picker"
                                :value="selected.start"
                                :class="pickerPosition"
                                :max-date="maxDate"
                                :min-date="minDate"
                                show
                                @dateSelected="dateStartSelected"
                            />
                        </div>
                        <div class="date-range-input__picker-wrap">
                            <span>{{ $t('dateRange.endDate') }}</span>
                            <Datepicker
                                class="date-range-input__picker"
                                :value="selected.end"
                                :class="pickerPosition"
                                :max-date="maxDate"
                                :min-date="minDate"
                                show
                                @dateSelected="dateEndSelected"
                            />
                        </div>
                    </div>
                    <div class="date-range-input__buttons">
                        <button type="button" class="button button--gray" @click.stop="cancel">
                            {{ $t('dateRange.clear') }}
                        </button>
                        <button
                            type="button"
                            class="button"
                            :disabled="!valid"
                            :class="{ 'is-disabled': !valid }"
                            @click.stop="apply"
                        >
                            {{ $t('dateRange.apply') }}
                        </button>
                    </div>
                </div>
            </transition>
            <input v-model="date" type="text" class="date-input__input" :required="required" />
            <transition name="fade">
                <span v-if="error" class="form-inpit-wrap__error">
                    {{ error[0] }}
                </span>
            </transition>
        </div>
    </div>
</template>

<script>
import Datepicker from '@speechki-frontend/shared/ui/Datepicker';
import { format } from 'date-fns';
import { clickOutside } from '@speechki-frontend/shared/helpers/directives/clickOutside';

const monthsMap = [
    {
        rus: 'янв',
        eng: 'Jan',
    },
    {
        rus: 'фев',
        eng: 'Feb',
    },
    {
        rus: 'мар',
        eng: 'Mar',
    },
    {
        rus: 'апр',
        eng: 'Apr',
    },
    {
        rus: 'май',
        eng: 'May',
    },
    {
        rus: 'июн',
        eng: 'Jun',
    },
    {
        rus: 'июл',
        eng: 'Jul',
    },
    {
        rus: 'авг',
        eng: 'Aug',
    },
    {
        rus: 'сен',
        eng: 'Sep',
    },
    {
        rus: 'окт',
        eng: 'Oct',
    },
    {
        rus: 'ноя',
        eng: 'Nov',
    },
    {
        rus: 'дек',
        eng: 'Dec',
    },
];

export default {
    name: 'DateInput',
    components: {
        Datepicker,
    },
    directives: {
        clickOutside,
    },
    props: {
        label: {
            type: String,
            default: '',
        },
        pickerPosition: {
            type: String,
            default: 'bottom',
        },
        required: {
            type: Boolean,
            default: false,
        },
        error: {
            type: Array,
            default: null,
        },
        modelValue: {},
        clearable: {
            type: Boolean,
            default: false,
        },
        minDate: {
            type: [Date, String, Number],
            default: 0,
        },
        maxDate: {
            type: [Date, String, Number],
            default: 99999999999999,
        },
    },
    emits: ['update:modelValue'],
    data() {
        return {
            showPicker: false,
            date: null,
            selected: {
                start: null,
                end: null,
            },
            dateText: '',
        };
    },
    computed: {
        range() {
            return `${format(this.selected.start, 'yyyy-MM-dd')}T00:00:00,${format(
                this.selected.end,
                'yyyy-MM-dd'
            )}T23:59:59`;
        },
        valid() {
            return (
                this.selected.start &&
                this.selected.end &&
                this.selected.start - this.selected.end <= 0
            );
        },
    },
    watch: {
        modelValue: {
            immidiate: true,
            handler() {
                if (this.modelValue && typeof this.modelValue === 'string') {
                    const splittedDate = this.modelValue.split(',');

                    this.selected.start = new Date(splittedDate[0]);
                    this.selected.end = new Date(splittedDate[1]);
                    this.setDateText();
                } else {
                    this.selected = {
                        start: null,
                        end: null,
                    };
                    this.dateText = this.$t('misc.date');
                }
            },
        },
    },
    created() {
        if (this.modelValue && typeof this.modelValue === 'string') {
            const splittedDate = this.modelValue.split(',');

            this.selected.start = new Date(splittedDate[0]);
            this.selected.end = new Date(splittedDate[1]);
            this.setDateText();
        } else {
            this.selected = {
                start: null,
                end: null,
            };
            this.dateText = this.$t('misc.date');
        }
    },
    methods: {
        dateStartSelected(date) {
            this.selected.start = new Date(date);
        },
        dateEndSelected(date) {
            this.selected.end = new Date(date);
        },
        apply() {
            this.$emit('update:modelValue', this.range);
            this.setDateText();
            this.showPicker = false;
        },
        cancel() {
            this.selected = {
                start: null,
                end: null,
            };
            this.$emit('update:modelValue', null);
            this.dateText = this.$t('misc.date');
            this.showPicker = false;
        },
        close() {
            this.showPicker = false;
        },
        setDateText() {
            if (!this.selected.start || !this.selected.end) return this.$t('misc.date');

            const dS = new Date(this.selected.start);
            let monthS = dS.getMonth();
            let dateS = dS.getDate();
            let yearS = dS.getFullYear();

            const dE = new Date(this.selected.end);
            let monthE = dE.getMonth();
            let dateE = dE.getDate();
            let yearE = dE.getFullYear();

            this.dateText = `${dateS} ${monthsMap[monthS][this.$i18n.locale]} ${yearS} - ${dateE} ${
                monthsMap[monthE][this.$i18n.locale]
            } ${yearE}`;
        },
    },
};
</script>
